import {
  CloudUploadOutlined,
  DownOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  PlusOutlined
} from "@ant-design/icons"
import { Button, Dropdown, Typography } from "antd"
import React, { Suspense, lazy, useState } from "react"
import { useTranslation } from "react-i18next"

import { uploadContract } from "../../../../Contracts/redux/contracts.thunks.ts"
import { TEMPLATE_DOCUMENT_TYPES } from "../../../../DocumentTemplates/constants/template-document-type.tsx"
import type {
  LeadContract,
  LeadProposal
} from "../../../types/lead.documents.types.ts"
import { DocumentCreateMenuOption } from "../../../LeadWorkspacePage/documents/DocumentCreateMenuOption.tsx"
import { LeadViewDocumentUpload } from "../LeadViewDocument/LeadViewDocumentUpload.tsx"
import { LeadViewContractsCreateModal } from "./LeadViewContractsCreateModal.tsx"

const LeadViewTemplatesModal = lazy(
  () => import("../LeadViewTemplatesModal/LeadViewTemplatesModal.tsx")
)

const { Text } = Typography

interface LeadViewContractsCreateTriggerProps {
  leadId: string
  proposals?: LeadProposal[] | null
  onCreateFromProposal: (proposalId: string) => Promise<void>
}

/**
 * All three existing ways to add a contract — from an existing proposal,
 * from a contract template, or an upload — unified under one "Create"
 * dropdown instead of split across a split-button's default action and a
 * separate top-level Upload button. Each option still opens the exact same
 * modal / runs the exact same upload thunk it always did; only the trigger
 * surface changed.
 */
export function LeadViewContractsCreateTrigger({
  leadId,
  proposals,
  onCreateFromProposal
}: LeadViewContractsCreateTriggerProps) {
  const { t } = useTranslation()
  const [showTemplatesModal, setShowTemplatesModal] = useState(false)
  const [showProposalsModal, setShowProposalsModal] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  const toggleTemplatesModal = () => setShowTemplatesModal((prev) => !prev)
  const toggleProposalsModal = () => setShowProposalsModal((prev) => !prev)

  return (
    <>
      {showTemplatesModal && (
        <Suspense>
          <LeadViewTemplatesModal
            leadId={leadId}
            type={TEMPLATE_DOCUMENT_TYPES.CONTRACT}
            onClose={toggleTemplatesModal}
          />
        </Suspense>
      )}

      {showProposalsModal && (
        <Suspense>
          <LeadViewContractsCreateModal
            leadId={leadId}
            proposals={proposals}
            onCreateFromProposal={onCreateFromProposal}
            onClose={toggleProposalsModal}
          />
        </Suspense>
      )}

      <Dropdown
        trigger={["click"]}
        open={menuOpen}
        onOpenChange={setMenuOpen}
        popupRender={() => (
          <div
            className={
              "w-80 rounded-card border border-border bg-white p-2 shadow-lg"
            }
          >
            <Text
              type={"secondary"}
              className={"text-xs px-2 block mb-1"}
            >
              {t("leadWorkspace.cards.documents.createContractMenuTitle")}
            </Text>
            <DocumentCreateMenuOption
              icon={<FileTextOutlined />}
              accent={"indigo"}
              title={t("leadWorkspace.cards.documents.fromExistingProposal")}
              description={t(
                "leadWorkspace.cards.documents.fromExistingProposalDescription"
              )}
              onClick={() => {
                setMenuOpen(false)
                toggleProposalsModal()
              }}
            />
            <DocumentCreateMenuOption
              icon={<FileDoneOutlined />}
              accent={"green"}
              title={t("leadWorkspace.cards.documents.fromContractTemplate")}
              description={t(
                "leadWorkspace.cards.documents.fromContractTemplateDescription"
              )}
              onClick={() => {
                setMenuOpen(false)
                toggleTemplatesModal()
              }}
            />
            <LeadViewDocumentUpload<LeadContract>
              leadId={leadId}
              documentType={"contract"}
              uploadThunk={uploadContract}
              render={() => (
                <DocumentCreateMenuOption
                  icon={<CloudUploadOutlined />}
                  accent={"orange"}
                  title={t("leadWorkspace.cards.documents.uploadContractOption")}
                  description={t(
                    "leadWorkspace.cards.documents.uploadContractOptionDescription"
                  )}
                />
              )}
            />
          </div>
        )}
      >
        <Button type={"primary"} icon={<PlusOutlined />}>
          {t("leadWorkspace.cards.documents.newContract")}
          <DownOutlined className={"text-xs"} />
        </Button>
      </Dropdown>
    </>
  )
}
